<template>
  <transition name="offline-banner">
    <div v-if="show" class="offline-banner" role="status" aria-live="polite">
      <ion-icon :icon="blocked ? shieldOutline : cloudOfflineOutline" aria-hidden="true" />
      <span>{{ message }}</span>
    </div>
  </transition>
</template>

<script setup lang="ts">
/**
 * Slim status strip pinned to the top of the app while the API is unreachable.
 *
 * Purely presentational — App.vue decides when to show it (and shifts the
 * router outlet down by the same height so nothing is covered). It owns the top
 * safe-area inset because it sits above every page header.
 *
 * `blocked` swaps the copy and icon when the API is unreachable because a
 * security check in front of the server answers in its place: "You're offline"
 * would send the user to fix a connection that is working.
 *
 * `pendingCount` (offline attendance) turns the offline line into "You're
 * offline — 2 punches waiting to sync." so the employee knows their punches
 * are safe on the device. Still ONE line in the same 32 px strip.
 */
import { computed } from 'vue';
import { IonIcon } from '@ionic/vue';
import { cloudOfflineOutline, shieldOutline } from 'ionicons/icons';
import { offlineBannerCopy } from '@/i18n/offline-attendance.en';

const props = withDefaults(defineProps<{ show: boolean; blocked?: boolean; pendingCount?: number }>(), {
  blocked: false,
  pendingCount: 0,
});

const message = computed(() => offlineBannerCopy({ blocked: props.blocked, pendingCount: props.pendingCount }));
</script>

<style scoped lang="scss">
.offline-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  /* Above page headers (z-index 10) and the update banner (200); Ionic
     overlays sit far higher, so modals and toasts still cover it. */
  z-index: 300;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  height: calc(32px + var(--syntry-safe-top, env(safe-area-inset-top, 0px)));
  padding-top: var(--syntry-safe-top, env(safe-area-inset-top, 0px));
  /* The strip's height is fixed (App.vue offsets the router outlet by exactly
     32px). Every copy (the waiting-punches line included — it is shorter than
     the security-check one) fits one line on a phone; the tight leading is the
     safety net for a larger system font that wraps one to two lines. */
  padding-inline: var(--sp-3);
  line-height: 1.2;
  text-align: center;
  background: var(--syntry-orange-soft);
  color: var(--syntry-text-strong);
  border-bottom: 1px solid var(--syntry-border);
  font-size: var(--syntry-fs-tiny);
  font-weight: 600;

  ion-icon {
    font-size: 14px;
    /* Keeps its size when the copy beside it needs the width to wrap. */
    flex-shrink: 0;
    color: var(--syntry-orange);
  }
}

.offline-banner-enter-active,
.offline-banner-leave-active {
  transition: transform 200ms ease, opacity 200ms ease;
}

.offline-banner-enter-from,
.offline-banner-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .offline-banner-enter-active,
  .offline-banner-leave-active {
    transition: none;
  }
}
</style>
